import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { useVenueId } from '../../hooks/useUserData';
import { createModifierGroup, fetchModifierGroups, updateModifierGroup } from '../../utils/menuService';
import '../../styles/modals/ModifierGroupsManager.css';

const EMPTY_GROUP_DRAFT = {
  id: null,
  name: '',
  required: false,
  minSelections: 0,
  maxSelections: 1,
  active: true,
  options: [
    {
      id: null,
      name: '',
      priceDelta: '0',
      sortOrder: 1,
      isDefault: false,
      active: true,
    },
  ],
};

const cloneGroupDraft = (group) => ({
  id: group?.id ?? null,
  name: group?.name ?? '',
  required: Boolean(group?.required),
  minSelections: group?.minSelections ?? 0,
  maxSelections: group?.maxSelections ?? 1,
  active: group?.active ?? true,
  options: (group?.options || []).map((option, index) => ({
    id: option?.id ?? null,
    name: option?.name ?? '',
    priceDelta: String(option?.priceDelta ?? 0),
    sortOrder: option?.sortOrder ?? index + 1,
    isDefault: option?.isDefault ?? false,
    active: option?.active ?? true,
  })),
});

const normalizeGroupPayload = (draft) => ({
  name: draft.name.trim(),
  required: Number(draft.minSelections || 0) > 0,
  minSelections: Number(draft.minSelections || 0),
  maxSelections: Number(draft.maxSelections || 1),
  active: Boolean(draft.active),
  options: draft.options.map((option, index) => ({
    ...(option.id ? { id: option.id } : {}),
    name: option.name.trim(),
    priceDelta: Number(option.priceDelta || 0),
    sortOrder: index + 1,
    isDefault: Boolean(option.isDefault),
    active: Boolean(option.active),
  })),
});

const validateGroupDraft = (draft) => {
  if (!draft.name.trim()) {
    return 'Grup adı gerekli.';
  }

  const minSelections = Number(draft.minSelections || 0);
  const maxSelections = Number(draft.maxSelections || 0);
  if (!Number.isFinite(minSelections) || !Number.isFinite(maxSelections)) {
    return 'Seçim kuralları geçersiz.';
  }
  if (minSelections > maxSelections) {
    return 'Minimum seçim, maksimum seçimden büyük olamaz.';
  }
  if (draft.required && minSelections < 1) {
    return 'Zorunlu grup için minimum seçim en az 1 olmalıdır.';
  }

  if (!draft.options.length) {
    return 'En az bir seçenek gerekli.';
  }

  if (draft.options.some((option) => !option.name.trim())) {
    return 'Tüm seçenek adları doldurulmalı.';
  }

  const defaultOptions = draft.options.filter((option) => option.isDefault);
  if (defaultOptions.length > 1) {
    return 'Bir grup için yalnızca bir varsayılan seçenek tanımlanabilir.';
  }
  if (defaultOptions.some((option) => option.active === false)) {
    return 'Varsayılan seçenek aktif olmalıdır.';
  }
  if (defaultOptions.length > 0 && Number(draft.maxSelections || 0) !== 1) {
    return 'Varsayılan seçenek yalnızca tek seçimli gruplarda kullanılabilir.';
  }

  return '';
};

const formatSelectionRule = (group) => {
  const minSelections = Number(group?.minSelections || 0);
  const maxSelections = Number(group?.maxSelections || 0);

  if (minSelections === maxSelections) {
    return `${maxSelections} seçim`;
  }

  if (minSelections === 0) {
    return `en fazla ${maxSelections} seçim`;
  }

  return `${minSelections}-${maxSelections} seçim`;
};

const formatGroupSummary = (group) => {
  const activeOptionsCount = (group?.options || []).filter((option) => option.active !== false).length;
  const summaryParts = [
    formatSelectionRule(group),
    `${activeOptionsCount} seçenek`,
  ];

  if (group?.active === false) {
    summaryParts.push('Pasif');
  }

  return summaryParts.join(' · ');
};

const ModifierGroupsManager = ({ value, onChange, modalVisible, onPersistAttachedGroups }) => {
  const venueId = useVenueId();
  const { getAccessTokenSilently } = useAuth0();
  const [availableGroups, setAvailableGroups] = useState([]);
  const [fetchError, setFetchError] = useState('');
  const [persistError, setPersistError] = useState('');
  const [editorVisible, setEditorVisible] = useState(false);
  const [editorDraft, setEditorDraft] = useState(EMPTY_GROUP_DRAFT);
  const [editorError, setEditorError] = useState('');
  const [savingEditor, setSavingEditor] = useState(false);
  const [persistingAttachments, setPersistingAttachments] = useState(false);
  const [selectedAttachId, setSelectedAttachId] = useState('');

  useEffect(() => {
    if (!modalVisible) {
      return;
    }

    let isMounted = true;
    fetchModifierGroups(venueId, getAccessTokenSilently)
      .then((groups) => {
        if (!isMounted) return;
        setAvailableGroups(Array.isArray(groups) ? groups : []);
        setFetchError('');
      })
      .catch((error) => {
        if (!isMounted) return;
        setFetchError(error.message || 'Modifier grupları yüklenemedi.');
      });

    return () => {
      isMounted = false;
    };
  }, [getAccessTokenSilently, modalVisible, venueId]);

  const attachedIds = useMemo(() => new Set((value || []).map((group) => group.id)), [value]);
  const attachableGroups = useMemo(
    () => availableGroups.filter((group) => !attachedIds.has(group.id)),
    [attachedIds, availableGroups]
  );

  const persistAttachedGroups = async (nextValue) => {
    const previousValue = value || [];
    onChange(nextValue);
    setPersistError('');

    if (!onPersistAttachedGroups) {
      return true;
    }

    setPersistingAttachments(true);
    try {
      const persistedGroups = await onPersistAttachedGroups(nextValue);
      if (persistedGroups) {
        onChange(persistedGroups);
      }
      return true;
    } catch (error) {
      onChange(previousValue);
      setPersistError(error.message || 'Ürün seçenekleri kaydedilemedi.');
      return false;
    } finally {
      setPersistingAttachments(false);
    }
  };

  const attachExistingGroup = async () => {
    const groupId = Number(selectedAttachId);
    if (!groupId) {
      return;
    }
    const group = availableGroups.find((entry) => entry.id === groupId);
    if (!group) {
      return;
    }
    const saved = await persistAttachedGroups([...(value || []), group]);
    if (saved) {
      setSelectedAttachId('');
    }
  };

  const openCreateEditor = () => {
    setEditorDraft(EMPTY_GROUP_DRAFT);
    setEditorError('');
    setEditorVisible(true);
  };

  const openEditEditor = (group) => {
    setEditorDraft(cloneGroupDraft(group));
    setEditorError('');
    setEditorVisible(true);
  };

  const handleEditorOptionChange = (index, patch) => {
    setEditorDraft((current) => ({
      ...current,
      options: current.options.map((option, optionIndex) =>
        optionIndex === index
          ? {
              ...option,
              ...patch,
              ...(patch.active === false ? { isDefault: false } : {}),
            }
          : patch.isDefault
            ? { ...option, isDefault: false }
            : option
      ),
    }));
  };

  const handleEditorSave = async () => {
    const validationError = validateGroupDraft(editorDraft);
    if (validationError) {
      setEditorError(validationError);
      return;
    }

    setSavingEditor(true);
    setEditorError('');

    try {
      const payload = normalizeGroupPayload(editorDraft);
      const savedGroup = editorDraft.id
        ? await updateModifierGroup(venueId, editorDraft.id, payload, getAccessTokenSilently)
        : await createModifierGroup(venueId, payload, getAccessTokenSilently);

      setAvailableGroups((current) => {
        const withoutCurrent = current.filter((group) => group.id !== savedGroup.id);
        return [...withoutCurrent, savedGroup].sort((left, right) => left.name.localeCompare(right.name, 'tr'));
      });

      if (editorDraft.id) {
        onChange((value || []).map((group) => (group.id === savedGroup.id ? savedGroup : group)));
      } else {
        const nextAttachedGroups = [...(value || []), savedGroup];
        const attached = await persistAttachedGroups(nextAttachedGroups);
        if (!attached) {
          return;
        }
      }

      setEditorVisible(false);
      setEditorDraft(EMPTY_GROUP_DRAFT);
    } catch (error) {
      setEditorError(error.message || 'Modifier grubu kaydedilemedi.');
    } finally {
      setSavingEditor(false);
    }
  };

  return (
    <>
      <div className="admin-form-block modifier-groups-manager">
        <div className="admin-form-block-title">Ürün Seçenekleri</div>
        <div className="modifier-groups-manager__toolbar">
          <Form.Group className="modifier-groups-manager__attach">
            <Form.Label>Grup ekle</Form.Label>
            <div className="modifier-groups-manager__attach-row">
              <Form.Select
                value={selectedAttachId}
                onChange={(event) => setSelectedAttachId(event.target.value)}
                className="form-input"
              >
                <option value="">Grup seçin</option>
                {attachableGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Form.Select>
              <button
                type="button"
                className="modifier-groups-manager__secondary-btn"
                onClick={attachExistingGroup}
                disabled={!selectedAttachId}
              >
                Bağla
              </button>
              <button
                type="button"
                className="modifier-groups-manager__link-btn"
                onClick={openCreateEditor}
              >
                Yeni grup
              </button>
            </div>
          </Form.Group>
        </div>

        {fetchError ? <div className="error-message mb-3">{fetchError}</div> : null}
        {persistError ? <div className="error-message mb-3">{persistError}</div> : null}

        {(value || []).length === 0 ? (
          <div className="modifier-groups-manager__empty">Henüz seçenek grubu eklenmedi.</div>
        ) : (
          <div className="modifier-groups-manager__list">
            {(value || []).map((group, index) => (
              <div key={group.id} className="modifier-group-row">
                <div className="modifier-group-row__main">
                  <div className="modifier-group-row__name">{group.name}</div>
                  <div className="modifier-group-row__meta">{formatGroupSummary(group)}</div>
                </div>
                <div className="modifier-group-row__actions">
                  <button
                    type="button"
                    className="modifier-group-row__icon-btn"
                    onClick={async () => {
                      if (index === 0) return;
                      const next = [...(value || [])];
                      [next[index - 1], next[index]] = [next[index], next[index - 1]];
                      await persistAttachedGroups(next);
                    }}
                    disabled={index === 0 || persistingAttachments}
                    aria-label={`${group.name} yukarı taşı`}
                    title="Yukarı taşı"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="modifier-group-row__icon-btn"
                    onClick={async () => {
                      if (index === (value || []).length - 1) return;
                      const next = [...(value || [])];
                      [next[index + 1], next[index]] = [next[index], next[index + 1]];
                      await persistAttachedGroups(next);
                    }}
                    disabled={index === (value || []).length - 1 || persistingAttachments}
                    aria-label={`${group.name} aşağı taşı`}
                    title="Aşağı taşı"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="modifier-group-row__text-btn"
                    onClick={() => openEditEditor(group)}
                  >
                    Düzenle
                  </button>
                  <button
                    type="button"
                    className="modifier-group-row__text-btn modifier-group-row__text-btn--danger"
                    onClick={() => persistAttachedGroups((value || []).filter((entry) => entry.id !== group.id))}
                    disabled={persistingAttachments}
                  >
                    Çıkar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        show={editorVisible}
        onHide={savingEditor ? undefined : () => setEditorVisible(false)}
        size="lg"
        centered
        className="modifier-group-editor-modal"
        backdropClassName="modifier-group-editor-backdrop"
      >
        <Modal.Header closeButton={!savingEditor}>
          <Modal.Title>{editorDraft.id ? 'Seçenek grubunu düzenle' : 'Yeni seçenek grubu'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column gap-3">
            <div className="d-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Form.Group>
                <Form.Label>Grup adı</Form.Label>
                <Form.Control
                  value={editorDraft.name}
                  onChange={(event) => setEditorDraft((current) => ({ ...current, name: event.target.value }))}
                  className="form-input"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Durum</Form.Label>
                <Form.Select
                  value={editorDraft.active ? 'active' : 'inactive'}
                  onChange={(event) => setEditorDraft((current) => ({ ...current, active: event.target.value === 'active' }))}
                  className="form-input"
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="d-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Form.Group>
                <Form.Label>Minimum seçim</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={editorDraft.minSelections}
                  onChange={(event) => setEditorDraft((current) => ({
                    ...current,
                    minSelections: event.target.value,
                    required: Number(event.target.value || 0) > 0,
                  }))}
                  className="form-input"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Maksimum seçim</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={editorDraft.maxSelections}
                  onChange={(event) => setEditorDraft((current) => {
                    const nextValue = event.target.value;
                    const nextMaxSelections = Number(nextValue || 0);
                    return {
                      ...current,
                      maxSelections: nextValue,
                      options:
                        nextMaxSelections === 1
                          ? current.options
                          : current.options.map((option) => ({ ...option, isDefault: false })),
                    };
                  })}
                  className="form-input"
                />
              </Form.Group>
            </div>

            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="fw-semibold">Seçenekler</div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() =>
                    setEditorDraft((current) => ({
                      ...current,
                      options: [
                        ...current.options,
                        {
                          id: null,
                          name: '',
                          priceDelta: '0',
                          sortOrder: current.options.length + 1,
                          isDefault: false,
                          active: true,
                        },
                      ],
                    }))
                  }
                >
                  Seçenek Ekle
                </Button>
              </div>
              {editorDraft.options.map((option, index) => (
                <div key={`${option.id || 'new'}-${index}`} className="modifier-group-editor-option">
                  <div className="modifier-group-editor-option__grid">
                    <Form.Group>
                      <Form.Label>Seçenek adı</Form.Label>
                      <Form.Control
                        value={option.name}
                        onChange={(event) => handleEditorOptionChange(index, { name: event.target.value })}
                        className="form-input"
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Fiyat farkı</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={option.priceDelta}
                        onChange={(event) => handleEditorOptionChange(index, { priceDelta: event.target.value })}
                        className="form-input"
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Durum</Form.Label>
                      <Form.Select
                        value={option.active ? 'active' : 'inactive'}
                        onChange={(event) => handleEditorOptionChange(index, { active: event.target.value === 'active' })}
                        className="form-input"
                      >
                        <option value="active">Aktif</option>
                        <option value="inactive">Pasif</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Varsayılan</Form.Label>
                      <Form.Check
                        type="switch"
                        checked={Boolean(option.isDefault)}
                        disabled={Number(editorDraft.maxSelections || 0) !== 1}
                        onChange={(event) => handleEditorOptionChange(index, { isDefault: event.target.checked })}
                        label=""
                      />
                    </Form.Group>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() =>
                        setEditorDraft((current) => ({
                          ...current,
                          options: current.options.filter((_, optionIndex) => optionIndex !== index),
                        }))
                      }
                      disabled={editorDraft.options.length === 1}
                    >
                      Sil
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {editorError ? <div className="error-message">{editorError}</div> : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditorVisible(false)} disabled={savingEditor}>
            Vazgeç
          </Button>
          <Button variant="primary" onClick={handleEditorSave} disabled={savingEditor}>
            {savingEditor ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ModifierGroupsManager;
